// Shared implementation of the customer + vendor mapping pages. The two
// pages are thin wrappers (they only differ in `kind` + heading copy);
// rendering the common form/table here keeps the surface in one place.

import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';

import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Table, type Column } from '../../../../components/ui/Table/Table';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import { ConfirmModal } from '../../../../components/ui/ConfirmModal/ConfirmModal';
import { Input } from '../../../../components/form/Input';
import { FormRow } from '../../../../components/form/FormRow';
import { useAuthStore } from '../../../../store/authStore';
import {
  useDeleteQbMapping,
  useQbMappings,
  useUpsertQbMapping,
} from './hooks/useQuickBooks';
import type { QbMappingDto, QbMappingKind } from '../../../../types/api';
import styles from './QuickBooksMappingPage.module.css';

const PAGE_SIZE = 25;

interface QuickBooksMappingPageProps {
  kind: QbMappingKind;
  title: string;
  subtitle: string;
  fieldLabel: string;
}

export function QuickBooksMappingPage(props: QuickBooksMappingPageProps): JSX.Element {
  const { kind, title, subtitle, fieldLabel } = props;
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const list = useQbMappings({ kind, page, limit: PAGE_SIZE });
  const upsert = useUpsertQbMapping();
  const remove = useDeleteQbMapping();

  const [customerName, setCustomerName] = useState('');
  const [qbId, setQbId] = useState('');
  const [pendingDelete, setPendingDelete] = useState<QbMappingDto | null>(null);

  function setPage(n: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(n));
    setParams(next, { replace: false });
  }

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!customerName.trim() || !qbId.trim()) return;
    upsert.mutate(
      { kind, customer_name: customerName.trim(), qb_id: qbId.trim() },
      {
        onSuccess: () => {
          setCustomerName('');
          setQbId('');
        },
      },
    );
  }

  const rows = list.data?.mappings ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<QbMappingDto>[] = [
    {
      key: 'customer_name',
      header: fieldLabel,
      render: (_v, row) => row.customer_name,
    },
    {
      key: 'qb_id',
      header: 'QuickBooks ID',
      render: (_v, row) => <code>{row.qb_id}</code>,
    },
    {
      key: 'updated_at',
      header: 'Updated',
      render: (_v, row) => formatDate(row.updated_at),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title={title} subtitle={subtitle} />

      {isAdmin ? (
        <Card title="Add a mapping">
          <form className={styles.form} onSubmit={handleAdd}>
            <FormRow>
              <Input
                label={fieldLabel}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
              <Input
                label="QuickBooks ID"
                value={qbId}
                onChange={(e) => setQbId(e.target.value)}
                helper={
                  kind === 'customer'
                    ? 'Customer ID from QuickBooks (e.g. 42).'
                    : 'Vendor ID from QuickBooks.'
                }
              />
            </FormRow>
            <div className={styles.formActions}>
              <Button
                variant="primary"
                icon={Plus}
                type="submit"
                loading={upsert.isPending}
                disabled={!customerName.trim() || !qbId.trim()}
              >
                Add mapping
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card title={`Mappings (${String(total)})`} flush>
        <Table
          columns={columns}
          data={rows}
          keyField={(r) => r.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No mappings yet</h3>
              <p>
                {isAdmin
                  ? 'Use the form above to map a customer to its QuickBooks counterpart.'
                  : 'No mappings have been created for this tenant.'}
              </p>
            </div>
          }
          rowActions={
            isAdmin
              ? (row) => [
                  {
                    label: 'Remove',
                    icon: Trash2,
                    variant: 'danger',
                    onClick: () => setPendingDelete(row),
                  },
                ]
              : undefined
          }
          ariaLabel={`${title} table`}
        />
        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? PAGE_SIZE, total, totalPages }}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Remove this mapping?"
        message={
          pendingDelete
            ? `${pendingDelete.customer_name} → ${pendingDelete.qb_id}`
            : ''
        }
        confirmLabel="Remove mapping"
        cancelLabel="Cancel"
        variant="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete === null) return;
          remove.mutate(pendingDelete, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
